import { AlertTriangle, Building, Calendar, Plus, Search,Tag, Users, WifiOff, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

// ✅ IMPORTAÇÃO CORRIGIDA: Adicionar função para obras pessoais
import { useAuth } from '@features/sistema/hooks/useAuth';
import { useOnlineStatus } from '@features/sistema/hooks/useOnlineStatus';
import { offlineSyncService } from '@features/sistema/services/offilineSyncService';
import { supabase } from '@features/sistema/services/supabaseClient';
// ❌ REMOVIDO: createTarefaComentario - não é mais usado (backend/trigger cuida dos comentários)
// Removido: dependência de minhasTarefasService (unificação pelo tarefasRepository)
import { createTask, getUsuariosAtivos } from '@features/tarefas/services/tarefasRepository';
import { Prioridade, prioridadeOptions } from '@features/tarefas/utils/tarefasUtils';
// ✅ NOVA IMPORTAÇÃO: Função para detectar obra pessoal
import { useIsMobile } from '@/features/sistema/hooks/useResponsive';

// ✅ CACHE SERVICE para modo offline
let __cacheSvc: any;
async function getCacheService() {
  if (!__cacheSvc) {
    const mod = await import('@features/sistema/services/cacheService');
    __cacheSvc = mod.cacheService;
    try { await __cacheSvc.init?.(); } catch {}
  }
  return __cacheSvc;
}

interface QuickTaskCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (nova?: any) => void; // ✅ agora recebe a tarefa criada
  obraPreSelecionada?: string;
  setorAtivo?: 'obra' | 'particular' | 'adm' | 'compras' | 'financeiro';
}

export default function QuickTaskCreator({
  isOpen,
  onClose,
  onSuccess,
  obraPreSelecionada,
  setorAtivo = 'obra'
}: QuickTaskCreatorProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { isOnline } = useOnlineStatus();
  // 🔧 Altura do teclado/área ocupada (visualViewport)
  const [kbInset, setKbInset] = useState(0);
  
  // Estados do formulário
  const [nome, setNome] = useState('');
  // ✅ MUDANÇA: Inicializar obraId apenas com obraPreSelecionada (obra pessoal será definida depois)
  const [obraId, setObraId] = useState(obraPreSelecionada || '');
  // ✅ MUDANÇA: data_inicio em vez de data_limite
  const [dataInicio, setDataInicio] = useState('');
  const [prioridade, setPrioridade] = useState<Prioridade>('baixa');
  // Busca e responsáveis (multi)
  const [buscaUsuario, setBuscaUsuario] = useState('');
  const [responsaveisIds, setResponsaveisIds] = useState<Set<string>>(new Set());
  
  // Estados de dados
  const [obras, setObras] = useState<any[]>([]);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // ✅ NOVO: Estado para armazenar ID da obra pessoal
  const [obraPessoalId, setObraPessoalId] = useState<string | null>(null);

  const carregarDados = useCallback(async () => {
    try {
      console.log(`📱 QuickTaskCreator: Carregando dados... (${isOnline ? 'Online' : 'Offline'})`);

      // ✅ CACHE-FIRST: Sempre carregar do cache primeiro
      let obrasFromCache: any[] = [];
      try {
        const cs = await getCacheService();
        obrasFromCache = await cs.getCachedObras();
        if (obrasFromCache.length > 0) {
          const obrasFiltradas = obrasFromCache.filter((obra: any) =>
            ['em_andamento', 'paralisada'].includes(obra.status)
          ).sort((a: any, b: any) => a.nome.localeCompare(b.nome));
          setObras(obrasFiltradas);
          console.log(`📱 QuickTaskCreator: ${obrasFiltradas.length} obras carregadas do cache`);
        }
      } catch (cacheError) {
        console.warn('⚠️ QuickTaskCreator: Erro ao acessar cache:', cacheError);
      }

      // Carregar obras do servidor se online
      if (setorAtivo === 'obra' && isOnline) {
        try {
          const { data: obrasData, error: obrasError } = await supabase
            .from('obras')
            .select('id, nome, status, is_personal')
            .in('status', ['em_andamento', 'paralisada'])
            .order('nome');

          if (obrasError) {
            console.error('Erro ao carregar obras:', obrasError);
            // Se falhou mas temos cache, usar o cache
            if (obrasFromCache.length === 0) {
              throw obrasError;
            }
          } else {
            let obrasFiltradas = obrasData || [];

            if (user?.id) {
              try {
                // ✅ CORREÇÃO: Criar obra pessoal diretamente usando a estrutura correta da tabela
                const { data: obraExistente } = await supabase
                  .from('obras')
                  .select('id, nome, is_personal')
                  .eq('is_personal', true)
                  .eq('supervisor_id', user.id)
                  .single();

                if (obraExistente) {
                  setObraPessoalId(obraExistente.id);
                  obrasFiltradas = (obrasData || []).filter((obra: any) => {
                    if (!obra.is_personal) {
                      return true;
                    }
                    return obra.is_personal && obra.id === obraExistente.id;
                  });
                } else {
                  // Se não existe, criar uma nova obra pessoal usando a estrutura correta
                  const { data: novaObra, error: errorCriacao } = await supabase
                    .from('obras')
                    .insert({
                      nome: '👤 Tarefas Pessoais',
                      is_personal: true,
                      status: 'em_andamento',
                      data_inicio: new Date().toISOString().split('T')[0], // Formato date
                      supervisor_id: user.id, // ✅ CORRETO: usar supervisor_id para obras pessoais
                      created_at: new Date().toISOString()
                    })
                    .select('id, nome, is_personal')
                    .single();

                  if (!errorCriacao && novaObra) {
                    setObraPessoalId(novaObra.id);
                    obrasFiltradas = (obrasData || []).filter((obra: any) => {
                      if (!obra.is_personal) {
                        return true;
                      }
                      return obra.is_personal && obra.id === novaObra.id;
                    });
                  } else {
                    obrasFiltradas = (obrasData || []).filter((obra: any) => !obra.is_personal);
                  }
                }
              } catch (error) {
                console.error('Erro ao obter obra pessoal:', error);
                obrasFiltradas = (obrasData || []).filter((obra: any) => !obra.is_personal);
              }
            } else {
              obrasFiltradas = (obrasData || []).filter((obra: any) => !obra.is_personal);
            }

            setObras(obrasFiltradas);

            // ✅ Atualizar cache em background
            getCacheService().then(async (cs) => {
              try {
                await cs.cacheObras(obrasData || []);
              } catch {}
            }).catch(() => {});
          }
        } catch (networkError) {
          console.warn('⚠️ QuickTaskCreator: Erro de rede, usando cache:', networkError);
        }
      } else if (setorAtivo === 'obra' && !isOnline) {
        // ✅ OFFLINE: Filtrar obras do cache corretamente
        if (user?.id && obrasFromCache.length > 0) {
          // Encontrar a obra pessoal do usuário atual
          const obraPessoal = obrasFromCache.find((obra: any) =>
            obra.is_personal && obra.supervisor_id === user.id
          );

          if (obraPessoal) {
            setObraPessoalId(obraPessoal.id);
          }

          // ✅ CORREÇÃO: Filtrar obras para mostrar apenas:
          // - Obras normais (não pessoais)
          // - A obra pessoal do usuário atual (se existir)
          const obrasFiltradas = obrasFromCache.filter((obra: any) => {
            // Obras normais: sempre incluir
            if (!obra.is_personal) return true;
            // Obras pessoais: apenas a do usuário atual
            return obra.is_personal && obra.supervisor_id === user.id;
          }).sort((a: any, b: any) => {
            // Colocar obra pessoal primeiro
            if (a.is_personal && !b.is_personal) return -1;
            if (!a.is_personal && b.is_personal) return 1;
            return (a.nome || '').localeCompare(b.nome || '');
          });

          setObras(obrasFiltradas);
          console.log(`📱 QuickTaskCreator (OFFLINE): ${obrasFiltradas.length} obras filtradas, obra pessoal: ${obraPessoal?.id || 'não encontrada'}`);
        }
      }

      // Carregar usuários (do cache ou servidor)
      try {
        if (isOnline) {
          const us = await getUsuariosAtivos(); // lista completa de ativos
          setUsuarios(us || []);
        } else {
          // ✅ OFFLINE: Usar usuário atual como fallback
          if (user) {
            setUsuarios([{ id: user.id, nome: user.email || 'Você', email: user.email }]);
          }
        }
      } catch (e) {
        console.error('Erro ao carregar usuários', e);
        // Fallback para usuário atual
        if (user) {
          setUsuarios([{ id: user.id, nome: user.email || 'Você', email: user.email }]);
        }
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    }
  }, [setorAtivo, isOnline, user]);

  // Carregar dados iniciais
  useEffect(() => {
    if (isOpen) {
      carregarDados();
      // ✅ MUDANÇA: Auto-definir data_inicio para hoje
      const hoje = new Date();
      hoje.setHours(9, 0, 0, 0);
      setDataInicio(hoje.toISOString().split('T')[0]);
    }
  }, [isOpen, carregarDados]);

  // 🔧 MOBILE: acompanha o teclado e ajusta o "bottom" do modal sem alterar o layout interno
  useEffect(() => {
    if (!isMobile) return;
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (!vv) return;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbInset(inset);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('focusin', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('focusin', update);
    };
  }, [isMobile]);

  // ✅ NOVO: Pré-selecionar obra pessoal quando disponível
  useEffect(() => {
    if (obraPessoalId && !obraPreSelecionada && setorAtivo === 'obra') {
      setObraId(obraPessoalId);
      console.log('🏠 Obra pessoal definida como pré-selecionada:', obraPessoalId);
    }
  }, [obraPessoalId, obraPreSelecionada, setorAtivo]);

  const usuariosFiltrados = useMemo(() => {
    const termo = buscaUsuario.trim().toLowerCase();
    if (!termo) return usuarios || [];
    return (usuarios || []).filter((u: any) =>
      String(u?.nome || '').toLowerCase().includes(termo) ||
      String(u?.email || '').toLowerCase().includes(termo)
    );
  }, [usuarios, buscaUsuario]);


  // 🔧 FUNÇÃO CORRIGIDA: handleSubmit com suporte OFFLINE
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!nome.trim()) {
      setErro('Nome da tarefa é obrigatório');
      return;
    }

    if (setorAtivo === 'obra' && !obraId) {
      setErro('Selecione uma obra');
      return;
    }

    try {
      setCriando(true);
      setErro(null);

      // responsáveis (multi) + fallback de principal (1º selecionado ou criador)
      const selecionados = Array.from(responsaveisIds);

      const obraParaCriar = setorAtivo === 'obra' ? obraId : (obraPessoalId || '');
      if (!obraParaCriar) throw new Error('Não foi possível determinar a obra para criação da tarefa.');

      // ✅ Buscar dados da obra selecionada para incluir no evento
      const obraSelecionada = obras.find(o => o.id === obraParaCriar);

      // ✅ CORREÇÃO: Se ninguém foi selecionado, auto-selecionar o criador como responsável
      const responsaveisFinal = selecionados.length > 0 ? selecionados : (user?.id ? [user.id] : []);
      const respIdFinal = responsaveisFinal[0] || user?.id || null;

      const payload = {
        nome: nome.trim(),
        obra_id: obraParaCriar,
        grupo_id: null,
        responsavel_id: respIdFinal ?? null,
        responsaveis_ids: responsaveisFinal,
        descricao: null,
        prioridade: prioridade,
        data_inicio: dataInicio || null,
        data_inicio_tem_hora: false, // ✅ CORREÇÃO: Sempre sem horário na criação rápida
        status: 'nao_iniciado' as const,
        ativo: true
      };

      let nova: any;

      if (isOnline) {
        // ✅ ONLINE: Criar normalmente via API
        nova = await createTask(payload);
        console.log('✅ Tarefa criada online com sucesso');
      } else {
        // ✅ OFFLINE: Criar via offlineSyncService
        console.log('📱 QuickTaskCreator: Modo offline, criando tarefa localmente...');

        const tempId = await offlineSyncService.createTarefaOffline({
          ...payload,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: user?.id || null,
          updated_by: user?.id || null
        });

        // Criar objeto de resposta simulado para a UI
        nova = {
          id: tempId,
          ...payload,
          obra: obraSelecionada ? {
            id: obraSelecionada.id,
            nome: obraSelecionada.nome,
            is_personal: obraSelecionada.is_personal
          } : { id: obraParaCriar, nome: 'Obra', is_personal: false },
          _offline: true, // Marca como criada offline
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        console.log('✅ Tarefa criada offline com ID temporário:', tempId);
      }

      // ✅ Avisar o app/páginas ouvintes (ex.: MinhasTarefas) SEM recarregar a página
      if (nova?.id) {
        window.dispatchEvent(
          new CustomEvent('tarefas:created', {
            detail: { id: nova.id, obra_id: obraParaCriar, tarefa: nova, offline: !isOnline }
          })
        );
      }

      // Limpa estados
      setNome('');
      setResponsaveisIds(new Set());
      setBuscaUsuario('');

      onSuccess?.(nova); // ✅ Entrega a tarefa criada para um refresh sutil
      onClose();

    } catch (err) {
      console.error('❌ Erro ao criar tarefa:', err);
      setErro((err as Error).message);
    } finally {
      setCriando(false);
    }
  }


  if (!isOpen) return null;

  const setorInfo = {
    obra: { name: 'Obra', icon: Building, color: 'text-blue-600 dark:text-blue-400' },
    particular: { name: 'Particular', icon: Users, color: 'text-green-600 dark:text-green-400' },
    adm: { name: 'ADM', icon: Tag, color: 'text-purple-600 dark:text-purple-400' },
    compras: { name: 'Compras', icon: Tag, color: 'text-orange-600 dark:text-orange-400' },
    financeiro: { name: 'Financeiro', icon: Tag, color: 'text-emerald-600 dark:text-emerald-400' }
  };

  const setor = setorInfo[setorAtivo];

  // Layout Mobile (Todoist-style)
  if (isMobile) {
    return createPortal(
      <div
        className="fixed inset-0 z-[2147483647] overflow-hidden"
        role="dialog"
        aria-modal="true"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={onClose} />
        
        {/* Modal estilo Todoist — sem mudar o layout interno; só sobe o container inteiro */}
        <div
          className="fixed left-0 right-0 bg-background dark:bg-gray-800 rounded-t-2xl shadow-xl border-t border-secondary dark:border-gray-700 transition-colors animate-slide-up"
          style={{
            // empurra o modal para cima: a base fica exatamente acima do teclado
            bottom: `calc(${kbInset}px + env(safe-area-inset-bottom))`
          }}
        >
          {/* Conteúdo rolável (mantido como estava; não alteramos a posição do botão) */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[72vh] overflow-y-auto">

            {/* ✅ INDICADOR OFFLINE */}
            {!isOnline && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <WifiOff className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="text-sm text-yellow-700 dark:text-yellow-400 font-ageo-regular">
                    Modo offline - tarefa será sincronizada quando conectar
                  </span>
                </div>
              </div>
            )}

            {/* Erro */}
            {erro && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
                  <span className="text-sm text-red-700 dark:text-red-400 font-ageo-regular">{erro}</span>
                </div>
              </div>
            )}
            
            {/* Nome da tarefa */}
            <input
              type="text"
              value={nome}
              onChange={(e) => {
                setNome(e.target.value);
                if (erro && e.target.value.trim()) setErro(null);
              }}
              placeholder="Nome da tarefa"
              className="w-full px-4 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-transparent font-ageo-regular text-base transition-colors"
              autoFocus
            />

            {/* Obra (se necessário) */}
            {setorAtivo === 'obra' && (
              <select
                value={obraId}
                onChange={(e) => {
                  setObraId(e.target.value);
                  if (erro && e.target.value) setErro(null);
                }}
                className="w-full px-4 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-transparent font-ageo-regular text-base transition-colors"
              >
                <option value="">Selecionar obra...</option>
                {obras.map((obra) => (
                  <option key={obra.id} value={obra.id}>
                    {obra.nome}
                  </option>
                ))}
              </select>
            )}

            {/* ✅ MUDANÇA: Data de Início (em vez de Vencimento) */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-1">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="w-full px-3 py-2 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-transparent font-ageo-regular text-sm transition-colors"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-1">
                  Prioridade
                </label>
                <select
                  value={prioridade}
                  onChange={(e) => setPrioridade(e.target.value as Prioridade)}
                  className="w-full px-3 py-2 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-transparent font-ageo-regular text-sm transition-colors"
                >
                  {prioridadeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Botão criar */}
            <button
              type="submit"
              disabled={criando || !nome.trim()}
              className="w-full px-4 py-3 bg-confirmation hover:bg-confirmation/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-ageo-bold text-base safe-area-bottom"
            >
              {criando ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Criando...
                </div>
              ) : (
                'Criar Tarefa'
              )}
            </button>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  // Layout Desktop (completo)
  return createPortal(
    <div
      className="fixed inset-0 z-[2147483647] overflow-hidden"
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-black/50 dark:bg-black/70 transition-opacity pointer-events-auto" onClick={onClose} />
        
        <div className="fixed inset-x-4 top-1/2 transform -translate-y-1/2 max-w-lg mx-auto">
          <div className="bg-background dark:bg-gray-800 rounded-2xl shadow-xl border border-secondary dark:border-gray-700 overflow-hidden transition-colors">
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-confirmation/10 dark:bg-green-900/20 rounded-lg">
                  <Plus className="w-5 h-5 text-confirmation dark:text-green-400" />
                </div>
                <div>
                  <h2 className="text-lg font-ageo-bold text-foreground dark:text-white">
                    Nova Tarefa
                  </h2>
                  <p className="text-sm text-foreground/70 dark:text-gray-300 flex items-center gap-1">
                    <setor.icon className={`w-4 h-4 ${setor.color}`} />
                    {setor.name}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-secondary/20 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <X className="w-5 h-5 text-foreground/70 dark:text-gray-200" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-3">

              {/* ✅ INDICADOR OFFLINE */}
              {!isOnline && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <WifiOff className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm text-yellow-700 dark:text-yellow-400 font-ageo-regular">
                      Modo offline - tarefa será sincronizada quando conectar
                    </span>
                  </div>
                </div>
              )}

              {/* Erro */}
              {erro && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
                    <span className="text-sm text-red-700 dark:text-red-400 font-ageo-regular">{erro}</span>
                  </div>
                </div>
              )}

              {/* Nome da tarefa */}
              <div>
                <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-2">
                  Nome da Tarefa *
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => {
                    setNome(e.target.value);
                    if (erro && e.target.value.trim()) setErro(null);
                  }}
                  placeholder="Digite o nome da tarefa..."
                  className="w-full px-3 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-primary dark:focus:border-blue-400 font-ageo-regular transition-colors"
                  autoFocus
                />
              </div>

              {/* Obra */}
              {setorAtivo === 'obra' && (
                <div>
                  <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-2">
                    Obra *
                  </label>
                  <select
                    value={obraId}
                    onChange={(e) => {
                      setObraId(e.target.value);
                      if (erro && e.target.value) setErro(null);
                    }}
                    className="w-full px-3 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-primary dark:focus:border-blue-400 font-ageo-regular transition-colors"
                  >
                    <option value="">Selecionar obra...</option>
                    {obras.map((obra) => (
                      <option key={obra.id} value={obra.id}>
                        {obra.nome} {obra.status === 'paralisada' && '(Paralisada)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* ✅ MUDANÇA: Data de Início + Prioridade */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-2">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full px-3 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-primary dark:focus:border-blue-400 font-ageo-regular transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-ageo-bold text-foreground dark:text-white mb-2">
                    Prioridade
                  </label>
                  <select
                    value={prioridade}
                    onChange={(e) => setPrioridade(e.target.value as Prioridade)}
                    className="w-full px-3 py-3 border border-secondary dark:border-gray-600 rounded-lg bg-background dark:bg-gray-800 text-foreground dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-blue-400 focus:border-primary dark:focus:border-blue-400 font-ageo-regular transition-colors"
                  >
                    {prioridadeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Responsáveis (multi) com busca */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground dark:text-white">
                    <Users className="w-4 h-4" />
                    <span>Responsáveis</span>
                  </div>
                  <div className="relative w-52">
                    <input
                      value={buscaUsuario}
                      onChange={(e) => setBuscaUsuario(e.target.value)}
                      placeholder="Buscar pessoa…"
                      className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-secondary dark:border-gray-600 bg-background dark:bg-gray-800 text-foreground dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    <Search className="absolute left-2 top-1.5 h-4 w-4 opacity-70" />
                  </div>
                </div>

                <div className="mt-2 max-h-56 overflow-auto rounded-md border border-secondary dark:border-gray-700 divide-y dark:divide-gray-700 bg-background dark:bg-gray-800 text-foreground dark:text-white">
                  {usuariosFiltrados.map((u:any) => {
                    const marcado = responsaveisIds.has(u.id);
                    return (
                      <div key={u.id} className="flex items-center gap-3 px-2 py-2 hover:bg-muted/60 dark:hover:bg-gray-700/60">
                        {/* Checkbox para selecionar múltiplos responsáveis */}
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={(ev) => {
                            const next = new Set(responsaveisIds);
                            if (ev.target.checked) next.add(u.id); else next.delete(u.id);
                            setResponsaveisIds(next);
                          }}
                          title="Selecionar responsável"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm leading-5 truncate text-foreground dark:text-white">{u.nome}</div>
                          <div className="text-xs text-muted-foreground dark:text-gray-300 truncate">{u.email}</div>
                        </div>
                      </div>
                    );
                  })}
                  {usuariosFiltrados.length === 0 && (
                    <div className="px-3 py-6 text-sm text-muted-foreground dark:text-gray-300">Nenhum usuário encontrado.</div>
                  )}
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={criando || !nome.trim()}
                  className="flex-1 px-4 py-3 bg-confirmation hover:bg-confirmation/90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-ageo-regular font-bold"
                >
                  {criando ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Criando...
                    </div>
              ) : (
                'Criar Tarefa'
              )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-3 bg-secondary dark:bg-gray-700 text-foreground dark:text-white rounded-lg hover:bg-secondary/80 dark:hover:bg-gray-600 transition-colors font-ageo-regular"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
